// Chakra imports
import {
  Box,
  Text,
  useColorModeValue,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  Divider,
  Icon,
  Button,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import React, { useState, useEffect, useRef } from "react";
import { MdPublic, MdAdd, MdDelete } from "react-icons/md";
import { formatDate } from "utils/format";
import regionsService from "services/regionsService";

export default function RegionsManagement() {
  const toast = useToast();
  const cancelRef = useRef();
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.100");
  const iconBoxBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const emptyIconBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const inputBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regions, setRegions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({ name: "", code: "" });
  const [formErrors, setFormErrors] = useState({});

  const fetchRegions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await regionsService.getRegions();
      if (result.success && result.data) setRegions(Array.isArray(result.data) ? result.data : []);
      else setError(result.error || "Failed to load regions");
    } catch (err) {
      console.error(err);
      setError("Failed to load regions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  const handleOpenCreate = () => {
    setFormData({ name: "", code: "" });
    setFormErrors({});
    onCreateOpen();
  };

  const handleCreate = async () => {
    const errors = {};
    if (!formData.name || !formData.name.trim()) errors.name = "Name is required";
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setCreating(true);
    setFormErrors({});
    try {
      const result = await regionsService.createRegion({
        name: formData.name.trim(),
        code: formData.code?.trim() || undefined,
      });
      if (result.success) {
        toast({
          title: "Success",
          description: result.message || "Region created successfully",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onCreateClose();
        fetchRegions();
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to create region",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to create region. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (region) => {
    setDeleteTarget(region);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await regionsService.deleteRegion(deleteTarget.id);
      if (result.success) {
        toast({
          title: "Success",
          description: result.message || "Region deleted",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onDeleteClose();
        setDeleteTarget(null);
        fetchRegions();
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to delete region",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to delete region. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Flex justify="center" align="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Flex>
      </Box>
    );
  }
  if (error) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Alert status="error" borderRadius="15px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} maxW="1000px" mx="auto">
      <Flex direction="column" gap="6">
        <Flex align="center" justify="space-between" flexWrap="wrap" gap="4">
          <Flex align="center" gap="3">
            <Flex w="12" h="12" borderRadius="xl" bg={iconBoxBg} align="center" justify="center">
              <Icon as={MdPublic} color="brand.500" boxSize="6" />
            </Flex>
            <Box>
              <Text color={textColor} fontSize="2xl" fontWeight="700">
                Regions
              </Text>
              <Text color={textColorSecondary} fontSize="sm">
                {regions.length} region{regions.length !== 1 ? "s" : ""}. Assign a region to each center.
              </Text>
            </Box>
          </Flex>
          <Button
            leftIcon={<Icon as={MdAdd} />}
            colorScheme="brand"
            size="md"
            borderRadius="lg"
            onClick={handleOpenCreate}
          >
            Add Region
          </Button>
        </Flex>

        {regions.length > 0 ? (
          <SimpleGrid columns={1} spacing="5">
            {regions.map((r) => (
              <Card
                key={r.id}
                p="5"
                bg={cardBg}
                borderRadius="16px"
                boxShadow="sm"
                borderWidth="1px"
                borderColor={borderColor}
                _hover={{ shadow: "md", borderColor: "brand.200" }}
              >
                <Flex direction="column" gap="3">
                  <Flex w="12" h="12" borderRadius="xl" bg={iconBoxBg} align="center" justify="center">
                    <Icon as={MdPublic} color="brand.500" boxSize="6" />
                  </Flex>
                  <Text color={textColor} fontSize="lg" fontWeight="700">
                    {r.name}
                  </Text>
                  {r.code && (
                    <Text color={textColorSecondary} fontSize="sm">
                      Code: {r.code}
                    </Text>
                  )}
                  <Divider borderColor={borderColor} />
                  <Flex justify="space-between" align="center">
                    <Text color={textColorSecondary} fontSize="xs">
                      Added {r.created_at ? formatDate(r.created_at) : "—"}
                    </Text>
                    <Button
                      size="xs"
                      leftIcon={<Icon as={MdDelete} />}
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => handleDeleteClick(r)}
                    >
                      Delete
                    </Button>
                  </Flex>
                </Flex>
              </Card>
            ))}
          </SimpleGrid>
        ) : (
          <Card p="0" bg={cardBg} borderRadius="16px" boxShadow="sm">
            <Flex direction="column" align="center" justify="center" py="16" px="6">
              <Flex w="16" h="16" borderRadius="full" bg={emptyIconBg} align="center" justify="center" mb="4">
                <Icon as={MdPublic} color={textColorSecondary} boxSize="8" />
              </Flex>
              <Text color={textColor} fontWeight="600" mb="1">
                No regions yet
              </Text>
              <Text color={textColorSecondary} fontSize="sm" textAlign="center" mb="4">
                Create regions first, then assign one to each center.
              </Text>
              <Button leftIcon={<Icon as={MdAdd} />} colorScheme="brand" size="sm" onClick={handleOpenCreate}>
                Add Region
              </Button>
            </Flex>
          </Card>
        )}
      </Flex>

      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>Add Region</ModalHeader>
          <ModalCloseButton color={textColor} />
          <ModalBody pb="6">
            <FormControl isRequired isInvalid={!!formErrors.name} mb="4">
              <FormLabel color={textColor}>Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (formErrors.name) setFormErrors({ ...formErrors, name: null });
                }}
                placeholder="e.g. North West"
                bg={inputBg}
                color={textColor}
              />
              {formErrors.name && (
                <Text color="red.500" fontSize="sm" mt="1">
                  {formErrors.name}
                </Text>
              )}
            </FormControl>
            <FormControl>
              <FormLabel color={textColor}>Code (optional)</FormLabel>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="e.g. NW"
                bg={inputBg}
                color={textColor}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>
              Cancel
            </Button>
            <Button colorScheme="brand" onClick={handleCreate} isLoading={creating} loadingText="Creating">
              Create Region
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader color={textColor}>Delete Region</AlertDialogHeader>
            <AlertDialogBody>
              Delete this region? If it is assigned to any center, the request will fail. Reassign those centers to another region first.
              {deleteTarget && (
                <Text color={textColorSecondary} fontSize="sm" mt="2">
                  {deleteTarget.name}
                  {deleteTarget.code ? ` (${deleteTarget.code})` : ""}
                </Text>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                ml={3}
                isLoading={deleting}
                loadingText="Deleting"
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
